import { OnInit, AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { default as SignaturePad } from 'signature_pad';
import { filter } from 'rxjs/operators';
import { dataURLToBlob } from 'blob-util';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { untilDestroyed } from '@app/core';
import { FontEnum, SignatureFormat, User, UserSignatureInput } from '@app/models';
import { NotyService, UserService } from '@app/services';
import { InitialsNamePipe } from '@app/shared/pipes/initials-name.pipe';
import { SignatureNamePipe } from '@app/shared/pipes/signature-name.pipe';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-signature-pad',
  templateUrl: './signature-pad.component.html',
  styleUrls: ['./signature-pad.component.scss'],
  standalone: false
})
export class SignaturePadComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('signatureCanvas', { static: false }) private signatureCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('signatureLine', { static: false }) private signatureLine: ElementRef<HTMLDivElement>;
  @ViewChild('initialsCanvas', { static: false }) private initialsCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('initialsLine', { static: false }) private initialsLine: ElementRef<HTMLDivElement>;

  @Input() isLoading: boolean;
  @Input() step: string;

  @Input() font: FontEnum | string;
  @Input() format: SignatureFormat | string;

  @Input() disableSignature: boolean;
  @Input() disableInitials: boolean;

  @Input() disableFormat: boolean;

  @Output() formatChange = new EventEmitter<SignatureFormat | string>();
  @Output() signatureTextChange = new EventEmitter<string>();
  @Output() initialsTextChange = new EventEmitter<string>();
  @Output() fontChange = new EventEmitter<string>();
  @Output() canSubmitForm = new EventEmitter<boolean>();
  @Output() isSignatureEmpty = new EventEmitter<boolean>();

  signatureNamePipe = new SignatureNamePipe();
  initialsPipe = new InitialsNamePipe();
  SignatureFormat = SignatureFormat;

  signatureForm: UntypedFormGroup;

  signaturePad: SignaturePad;
  initialsPad: SignaturePad;

  selectedSignatureImageUrl: string;
  selectedInitialsImageUrl: string;

  currentUser: User;
  isPadOn = true;

  isMobile: boolean;

  fonts = [
    { key: FontEnum.Autograf, value: 'Autograf' },
    { key: FontEnum.Fathur, value: 'Fathur' },
    { key: FontEnum.Robertson, value: 'Robertson' }
  ];

  selectedSignatureImage: File;
  selectedInitialsImage: File;

  isDraggingSignature = false;
  isDraggingInitials = false;

  private acceptedTypes = ['image/png', 'image/jpeg', 'image/jpg'];

  constructor(
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private notyService: NotyService,
    private translateService: TranslateService
  ) {
    this.signatureForm = this.formBuilder.group({
      user: this.formBuilder.group({
        format: SignatureFormat.Handwriting,
        font: FontEnum.Autograf,
        initials_text: '',
        signature_text: ''
      } as UserSignatureInput)
    });
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;
    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => {
          this.currentUser = user as User;

          this.selectedSignatureImageUrl = this.currentUser.settings.signature;
          this.selectedInitialsImageUrl = this.currentUser.settings.initials;

          this.signatureForm.get('user')?.patchValue({
            format: this.currentUser.settings.format,
            font: this.currentUser.settings.font,
            initials_text: this.currentUser.settings.initials_text || this.initialsPipe.transform(this.currentUser.name),
            signature_text: this.currentUser.settings.signature_text || this.signatureNamePipe.transform(this.currentUser.name)
          });

          this.afterCheck(() => this.isMissingInfo());
        }
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.resizePad();
      if (this.signatureCanvas) {
        this.signaturePad = new SignaturePad(this.signatureCanvas.nativeElement, { penColor: '#1c324d' });
        this.signaturePad.onEnd = () => this.isMissingInfo();
      }
      if (this.initialsCanvas) {
        this.initialsPad = new SignaturePad(this.initialsCanvas.nativeElement, { penColor: '#1c324d' });
        this.initialsPad.onEnd = () => this.isMissingInfo();
      }
      // Bloq pad if format is not Draw
      this.togglePad([this.currentUser?.settings?.format, this.format].includes(SignatureFormat.Draw));
      try {
        (document.activeElement as HTMLElement).blur();
      } catch (e) {}
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.format && changes.format.currentValue) {
      this.afterCheck(() => this.selectPreviewMode(changes.format.currentValue));
      // Bloq/Unbloq pad according to the format
      this.togglePad([this.currentUser?.settings?.format, this.format].includes(SignatureFormat.Draw));
    }
    if (changes.isLoading) {
      this.togglePad(!changes.isLoading.currentValue);
    }
    if (changes.step && changes.step.currentValue != null) {
      const stepValue = changes.step.currentValue;
      this.afterCheck(() => {
        this.disableInitials = stepValue === 'signature';
        this.disableSignature = stepValue === 'initials';
        this.isMissingInfo();
      });
    }
  }

  ngOnDestroy() {
    URL.revokeObjectURL(this.selectedSignatureImageUrl);
    URL.revokeObjectURL(this.selectedInitialsImageUrl);
  }

  togglePad(isEnabled: boolean) {
    if (this.signaturePad) {
      isEnabled ? this.signaturePad.on() : this.signaturePad.off();
    }
    if (this.initialsPad) {
      isEnabled ? this.initialsPad.on() : this.initialsPad.off();
    }
  }

  clearSignature() {
    if (this.signaturePad) {
      this.signaturePad.clear();
    }
  }

  clearInitials() {
    if (this.initialsPad) {
      this.initialsPad.clear();
    }
  }

  clear() {
    this.clearSignature();
    this.clearInitials();
  }

  isEmptySignature() {
    return (
      !this.signaturePad ||
      (this.format === SignatureFormat.Draw && this.signaturePad.isEmpty()) ||
      (this.format === SignatureFormat.Image && !this.selectedSignatureImage && !this.selectedSignatureImageUrl)
    );
  }

  isEmptyInitials() {
    return (
      !this.initialsPad ||
      (this.format === SignatureFormat.Draw && this.initialsPad.isEmpty()) ||
      (this.format === SignatureFormat.Image && !this.selectedInitialsImage && !this.selectedInitialsImageUrl)
    );
  }

  isEmpty() {
    return (!this.disableSignature && (this.isEmptySignature() || this.isMinSize('signature'))) || (!this.disableInitials && (this.isEmptyInitials() || this.isMinSize('initials')));
  }

  isMinSize(type: 'signature' | 'initials') {
    const sizes = this.canvasSizes((type === 'signature' ? this.signatureCanvas : this.initialsCanvas).nativeElement);
    return sizes.trimWidth < 30 || sizes.trimHeight < 30;
  }

  toImageFile(type: 'signature' | 'initials', fileType: string = 'image/png', fileName: string = 'drawn-signature.png') {
    if (this.format === SignatureFormat.Image) {
      return type === 'signature' ? this.selectedSignatureImage : this.selectedInitialsImage;
    } else if (this.format === SignatureFormat.Draw) {
      return (type === 'signature' && !this.isEmptySignature()) || (type === 'initials' && !this.isEmptyInitials())
        ? new File([dataURLToBlob(this.trimCanvas((type === 'signature' ? this.signatureCanvas : this.initialsCanvas).nativeElement).toDataURL(fileType))], fileName, {
            lastModified: new Date().getTime()
          })
        : null;
    }
  }

  selectPreviewMode(format: SignatureFormat) {
    this.format = format;
    this.isMissingInfo();

    // emitir somente após o ciclo atual
    this.afterCheck(() => this.formatChange.emit(this.format));

    if (!this.format || this.format === SignatureFormat.Draw) {
      this.isPadOn = true;
      if (this.signaturePad) {
        this.signaturePad.on();
        // Inicia canvas com imagem desenhada, mas não funciona porquê dá erro "The canvas has been tainted by cross-origin data"
        // if (this.signaturePad.isEmpty() && this.currentUser.settings.signature_draw) {
        //   this.signaturePad.fromDataURL(this.currentUser.settings.signature_draw);
        // }
      }
      if (this.initialsPad) {
        this.initialsPad.on();
        // Inicia canvas com imagem desenhada, mas não funciona porquê dá erro "The canvas has been tainted by cross-origin data"
        // if (this.initialsPad.isEmpty() && this.currentUser.settings.initials_draw) {
        //   this.initialsPad.fromDataURL(this.currentUser.settings.initials_draw);
        // }
      }
    } else {
      this.clear();
      this.isPadOn = false;
      if (this.signaturePad) {
        this.signaturePad.off();
      }
      if (this.initialsPad) {
        this.initialsPad.off();
      }
    }
  }

  selectSignatureImage(image: File) {
    if (image) {
      URL.revokeObjectURL(this.selectedSignatureImageUrl);
      this.selectedSignatureImage = image;
      this.selectedSignatureImageUrl = URL.createObjectURL(image);
      this.isMissingInfo();
    }
  }

  selectInitialsImage(image: File) {
    if (image) {
      URL.revokeObjectURL(this.selectedInitialsImageUrl);
      this.selectedInitialsImage = image;
      this.selectedInitialsImageUrl = URL.createObjectURL(image);
      this.isMissingInfo();
    }
  }

  isMissingInfo() {
    const user = this.signatureForm.get('user')?.value;

    const isEmpty = (val: string) => !val?.trim();
    const hasNumbers = (val: string) => /\d/.test(val);

    let missingSignature = false;
    let missingInitials = false;

    switch (this.format) {
      case SignatureFormat.Draw:
        if (!this.disableSignature) {
          missingSignature = this.isEmptySignature() || this.isMinSize('signature');
        }
        if (!this.disableInitials) {
          missingInitials = this.isEmptyInitials() || this.isMinSize('initials');
        }
        break;

      case SignatureFormat.Handwriting:
        if (!this.disableSignature) {
          missingSignature = isEmpty(user.signature_text) || hasNumbers(user.signature_text);
        }
        if (!this.disableInitials) {
          missingInitials = isEmpty(user.initials_text) || hasNumbers(user.initials_text);
        }
        break;

      case SignatureFormat.Eletronic:
        if (!this.disableInitials) {
          missingInitials = isEmpty(user.initials_text) || hasNumbers(user.initials_text);
        }
        break;

      case SignatureFormat.Image:
        if (!this.disableSignature) {
          missingSignature = !this.selectedSignatureImage && !this.selectedSignatureImageUrl;
        }
        if (!this.disableInitials) {
          missingInitials = !this.selectedInitialsImage && !this.selectedInitialsImageUrl;
        }
        break;

      default:
        break;
    }

    const missing = missingSignature || missingInitials;
    this.afterCheck(() => this.canSubmitForm.emit(!missing));
  }

  emitSignatureText(event: string) {
    this.isMissingInfo();
    this.afterCheck(() => this.signatureTextChange.emit(event));
  }

  emitInitialsText(event: string) {
    this.isMissingInfo();
    this.afterCheck(() => this.initialsTextChange.emit(event));
  }

  blockInvalidChars(event: KeyboardEvent) {
    const allowedRegex = /^[a-zA-ZÀ-ÿ\s]$/;
    if (!allowedRegex.test(event.key)) {
      event.preventDefault();
    }
  }

  onDragOver(event: DragEvent, type: 'signature' | 'initials') {
    if (!this.isDropEnabled(type)) return;
    event.preventDefault();
    event.stopPropagation();
    this.setDragging(type, true);
  }

  onDragLeave(event: DragEvent, type: 'signature' | 'initials') {
    if (!this.isDropEnabled(type)) return;
    event.preventDefault();
    event.stopPropagation();
    this.setDragging(type, false);
  }

  onDrop(event: DragEvent, type: 'signature' | 'initials') {
    if (!this.isDropEnabled(type)) return;
    event.preventDefault();
    event.stopPropagation();
    this.setDragging(type, false);

    const file = this.extractFirstImageFile(event.dataTransfer);
    if (!file) {
      return;
    }

    if (type === 'signature') {
      this.selectSignatureImage(file);
    } else {
      this.selectInitialsImage(file);
    }
  }

  private resizePad() {
    if (
      this.signatureCanvas &&
      this.signatureCanvas.nativeElement.parentElement.offsetWidth > 0 &&
      this.signatureCanvas.nativeElement.width !== this.signatureCanvas.nativeElement.parentElement.offsetWidth
    ) {
      this.signatureCanvas.nativeElement.width = this.signatureCanvas.nativeElement.parentElement.offsetWidth;
      this.signatureCanvas.nativeElement.height = this.signatureCanvas.nativeElement.parentElement.offsetHeight;
      this.clearSignature();
    }
    if (
      this.initialsCanvas &&
      this.initialsCanvas.nativeElement.parentElement.offsetWidth > 0 &&
      this.initialsCanvas.nativeElement.width !== this.initialsCanvas.nativeElement.parentElement.offsetWidth
    ) {
      this.initialsCanvas.nativeElement.width = this.initialsCanvas.nativeElement.parentElement.offsetWidth;
      this.initialsCanvas.nativeElement.height = this.initialsCanvas.nativeElement.parentElement.offsetHeight;
      this.clearInitials();
    }
  }

  private canvasSizes(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const length = pixels.data.length;
    const bound: { top?: number; bottom?: number; left?: number; right?: number } = {};

    // Iterate over every pixel to find the highest
    // and where it ends on every axis ()
    for (let i = 0; i < length; i += 4) {
      if (pixels.data[i + 3] !== 0) {
        const x = (i / 4) % canvas.width;
        const y = Math.floor(i / 4 / canvas.width);

        if (bound.top === undefined) {
          bound.top = y;
        }

        if (bound.left === undefined) {
          bound.left = x;
        } else if (x < bound.left) {
          bound.left = x;
        }

        if (bound.right === undefined) {
          bound.right = x;
        } else if (bound.right < x) {
          bound.right = x;
        }

        if (bound.bottom === undefined) {
          bound.bottom = y;
        } else if (bound.bottom < y) {
          bound.bottom = y;
        }
      }
    }

    // Calculate the height and width of the content
    const trimHeight = bound.bottom - bound.top;
    const trimWidth = bound.right - bound.left;
    return { boundTop: bound.top, boundBottom: bound.bottom, boundLeft: bound.left, boundRight: bound.right, trimWidth, trimHeight };
  }

  private trimCanvas(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    const copy = document.createElement('canvas').getContext('2d');
    const sizes = this.canvasSizes(canvas);
    const trimmed = ctx.getImageData(sizes.boundLeft, sizes.boundTop, sizes.trimWidth, sizes.trimHeight);

    copy.canvas.width = sizes.trimWidth;
    copy.canvas.height = sizes.trimHeight;
    copy.putImageData(trimmed, 0, 0);

    return copy.canvas;
  }

  private afterCheck(fn: () => void) {
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(fn);
    } else {
      Promise.resolve().then(fn);
    }
  }

  private isDropEnabled(type: 'signature' | 'initials') {
    if (this.format !== SignatureFormat.Image) return false;
    if (type === 'signature') return !this.disableSignature;
    return !this.disableInitials;
  }

  private setDragging(type: 'signature' | 'initials', value: boolean) {
    if (type === 'signature') {
      this.isDraggingSignature = value;
    } else {
      this.isDraggingInitials = value;
    }
  }

  private extractFirstImageFile(dt?: DataTransfer | null): File | null {
    if (!dt?.files?.length) {
      this.notyService.error(this.translateService.instant('signature.wrongImageFormat'));
      return null;
    }

    const file = dt.files[0];

    const nameLower = (file.name || '').toLowerCase();
    const looksLikeImage = this.acceptedTypes.includes(file.type) || nameLower.endsWith('.png') || nameLower.endsWith('.jpg') || nameLower.endsWith('.jpeg');

    if (!looksLikeImage) {
      this.notyService.error(this.translateService.instant('signature.wrongImageFormat'));
      return null;
    }

    if (file.size > 500 * 1024) {
      this.notyService.error(this.translateService.instant('signature.fileTooLarge', { maxSize: '500KB' }));
      return null;
    }

    return file;
  }

  @HostListener('window:resize') onResize() {
    this.resizePad();
  }

  @HostListener('document:dragover', ['$event'])
  onDocumentDragOver(event: DragEvent) {
    event.preventDefault();
  }

  @HostListener('document:drop', ['$event'])
  onDocumentDrop(event: DragEvent) {
    event.preventDefault();
  }
}
